import { useMemo } from 'react';
import { Search } from 'lucide-react';
import { useTranslation } from 'react-i18next';

import { Table } from '@Components';
import { Button } from '@Components/ui-v2/button';
import { Input } from '@Components/ui-v2/input';
import { Store } from '@Models';
import { TABLE_HEADER_CELL_CLASS_NAME } from '../../../FieldServices/constants';
import { getStoreKey } from '../constants';
import Checkbox from './checkbox';

const PAGE_SIZE = 50;

interface StoreTableProps {
  search: string;
  onSearchChange: (search: string) => void;
  searchedStores: Store[];
  selectedKeys: Set<string>;
  isSelectable: (store: Store) => boolean;
  oneRetailerHint: string;
  onToggleStore: (store: Store) => void;
  selectableCount: number;
  allSearchedSelected: boolean;
  selectAllDisabled: boolean;
  onToggleSelectAll: () => void;
}

const HEADER_CELL_CLASS_NAME = `px-4 py-2.5 ${TABLE_HEADER_CELL_CLASS_NAME}`;
const CELL_CLASS_NAME = 'px-4 py-2.5 text-xs';

const StoreTable = ({
  search,
  onSearchChange,
  searchedStores,
  selectedKeys,
  isSelectable,
  oneRetailerHint,
  onToggleStore,
  selectableCount,
  allSearchedSelected,
  selectAllDisabled,
  onToggleSelectAll,
}: StoreTableProps) => {
  const { t } = useTranslation();
  const shownCount = Math.min(PAGE_SIZE, searchedStores.length);

  const columns = useMemo(
    () => [
      {
        Header: '',
        accessor: 'select',
        width: 'w-10',
        Cell: ({ row }: { row: { original: Store } }) => (
          <Checkbox
            size="md"
            checked={selectedKeys.has(getStoreKey(row.original))}
            indeterminate={false}
            disabled={!isSelectable(row.original)}
            onChange={() => onToggleStore(row.original)}
            onClick={(event) => event.stopPropagation()}
          />
        ),
      },
      {
        Header: t('Schedule.Wizard.Where.ColumnStore'),
        accessor: 'name',
        Cell: ({ row }: { row: { original: Store } }) => (
          <span className="text-[13px] font-semibold text-gray-900">
            {row.original.name ?? row.original.location_code}
          </span>
        ),
      },
      {
        Header: t('Schedule.Wizard.Where.ColumnRegion'),
        accessor: 'region',
        Cell: ({ row }: { row: { original: Store } }) => row.original.region ?? '—',
      },
    ],
    [t, selectedKeys, isSelectable, onToggleStore],
  );

  return (
    <div className="flex min-w-0 flex-1 flex-col gap-3 p-4">
      <div className="relative w-full">
        <Search size={14} className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
        <Input
          type="text"
          value={search}
          onChange={(event) => onSearchChange(event.target.value)}
          placeholder={t('Schedule.Wizard.Where.StoreSearchPlaceholder')}
          className="h-9 pl-9 text-xs md:text-xs"
        />
      </div>

      <div className="h-[420px] overflow-y-auto">
        {searchedStores.length === 0 ? (
          <p className="rounded-md border border-dashed border-gray-200 bg-gray-50 px-3 py-6 text-center text-sm text-gray-500">
            {t('Schedule.Wizard.Where.NoStores')}
          </p>
        ) : (
          <Table
            columns={columns}
            data={searchedStores.slice(0, PAGE_SIZE)}
            pageSize={PAGE_SIZE}
            headerCellClassName={HEADER_CELL_CLASS_NAME}
            cellClassName={CELL_CLASS_NAME}
            getRowClassName={(row: { original: Store }) =>
              isSelectable(row.original) ? 'cursor-pointer' : 'cursor-not-allowed'
            }
            getRowProps={(row: { original: Store }) =>
              isSelectable(row.original) ? { onClick: () => onToggleStore(row.original) } : { title: oneRetailerHint }
            }
          />
        )}
      </div>

      <div className="flex items-center justify-between">
        <span className="text-xs text-gray-400">
          {t('Schedule.Wizard.Where.ShowingCount', { shown: shownCount, total: searchedStores.length })}
        </span>
        {searchedStores.length > 0 && (
          <Button
            type="button"
            variant="outline"
            size="sm"
            className="h-8 text-xs font-semibold"
            disabled={selectAllDisabled}
            title={selectAllDisabled ? oneRetailerHint : undefined}
            onClick={onToggleSelectAll}>
            {allSearchedSelected
              ? t('Schedule.Wizard.Where.ClearAll')
              : t('Schedule.Wizard.Where.SelectAll', { count: selectableCount })}
          </Button>
        )}
      </div>
    </div>
  );
};

export default StoreTable;
